/**
 * `chat` namespace catalog (ADR 0065) — user-facing strings for the chat area
 * (`src/chat/`). FLAT camelCase keys, one per line. The namespace is derived
 * from the path (`src/chat/i18n/en.ts` → `chat`). Components read these with
 * `useTranslation('chat')`; non-component modules via `i18n.t('chat:<key>')`.
 *
 * Generic actions/states (save/cancel/close/delete/retry/loading/none …) come
 * from the shared `common` namespace and are NOT duplicated here.
 */
export const messages = {
  planPreviewLoading: 'Checking what this would change…',
  planPreviewFailed: 'We couldn’t work out what this would change. You can still accept or dismiss — accepting re-checks your plan first.',
  planPreviewAdviceOnly: 'This is advice only — it carries no changes to apply.',
  planPreviewHeading: 'If you accept: {{count}} change to your plan',
  planPreviewHeading_other: 'If you accept: {{count}} changes to your plan',
  voiceRtOnbGetKey: 'Get a key',
  // ADR 0141 real-time voice onboarding
  voiceRtOnbTitle: "Talk to the AI in real time",
  voiceRtOnbBody: "Real-time voice is a natural spoken conversation — you talk, the assistant talks back, and you can interrupt. It uses a real-time AI provider with your own API key (BYOK); your key stays on the server and is never shared.",
  voiceRtOnbProviders: "Choose a provider",
  voiceRtOnbOpenai: "OpenAI Realtime — low-latency voice over WebRTC.",
  voiceRtOnbGemini: "Gemini Live — Google’s live voice over WebSocket.",
  voiceRtOnbHow: "To turn it on",
  voiceRtOnbStep1: "Add your provider API key on the Keys page.",
  voiceRtOnbStep2: "Choose the provider + key under “Real-time voice”.",
  voiceRtOnbSetup: "Set it up",
  voiceRtOnbLater: "Use recorded voice for now",

  // ADR 0138 voice mode (dynamic t() in VoiceModeButton)
  // — ChatHeader —
  closeChatTools: 'Close chat tools',
  openChatTools: 'Open chat tools',
  chatToolsTitle: 'History, workflow progress, and active agents',
  webSearchOnTitle: 'Web search ON — next turn uses provider-native search',
  webSearchOffTitle: 'Web search off (click to enable)',
  toggleWebSearch: 'Toggle web search',
  webLabel: 'Web search',
  toolsOnTitle: 'Tools ON — the AI can call saved workflows as tools this turn',
  toolsOffTitle: 'Tools off (click to enable). Lets the AI invoke saved workflows.',
  toggleWorkflowTools: 'Toggle workflow tools',
  toolsLabel: 'Tools',
  onSuffix: 'on',
  permissionSafeLabel: 'Ask first',
  permissionBypassLabel: 'Act directly',
  permissionSafeTitle: 'Ask first — the agent asks before running code or other consequential actions (click to let it act directly)',
  permissionBypassTitle: 'Act directly — the agent acts without asking you first (workspace approval rules, sandbox, permissions & limits still apply; click to ask first)',
  togglePermissionMode: 'Toggle permission mode (safe / bypass)',
  permissionOwnerOnlyTitle: 'Ask first — only the conversation owner can let the agent act directly',
  permissionBypassHint: 'Acting directly — the agent acts without asking you first. Workspace approval rules, sandbox, permissions and limits still apply.',
  totalSessionCost: 'Total session cost: {{cost}}',
  sessionCostLabel: 'Σ {{cost}}',
  newChat: 'New chat',
  moreActions: 'More actions',

  // — ChatSidebar —
  turnInFlight: 'A turn is in flight — wait for the response.',
  noProjectAgents: 'No agents on this project are available yet — add some on the Members tab to convene the team.',
  // `COLWF-2` — the project lane's partial-cohort disclosure. Deliberately NOT the board
  // wording: these are project teammates, not advisors.
  conveneProjectPartial: 'Convened {{activated}} of {{total}} project agents — the others could not be reached and did not take a turn.',
  conveneModeratorUnavailable: 'The moderator you chose for this project is not available in this conversation, so nothing was convened. Choose a different moderator on the Chat tab, or clear it, and try again.',
  // ADR 0665 D1 — the BOARD lane's own wording: the project string says "project".
  conveneBoardModeratorUnavailable: 'The moderator this board declares is not available in this conversation, so nothing was convened. The moderator both opens the discussion and writes the summary, so an advisor was not substituted. Choose a different moderator for the board, or clear it, and try again.',
  conveneTeamFailed: 'Couldn’t convene the project team: {{error}}',
  conveneBoardNoAdvisors: 'Convened @@{{handle}}, but none of its advisors are available as agents yet.',
  conveneBoardPartial: 'Convened @@{{handle}} with {{activated}} of {{total}} advisors — the rest aren’t available as agents yet.',
  conveneBoardFailed: 'Couldn’t convene @@{{handle}}: {{error}}',
  noWorkspaceAssistant: 'No workspace assistant is configured for this workspace yet.',
  conveneTheTeam: 'Convene the team',
  conveneNoProject: "Type `@@<board-handle>` to convene a board. A bare `@@` convenes a project's team — only inside a project conversation.",
  conveneOpener: 'Let’s convene the team — where do things stand on this project, and what needs attention?',
  advisorFallbackPersona: 'Advisor',
  conveneHelpPrefix: 'Each project agent weighs in, one at a time — or start a message with ',
  conveneHelpSuffix: ' to convene on a topic.',
  generatingPlaceholder: 'Generating… (Esc to stop)',
  composerPlaceholder: 'Type / for commands + workflows, @ for agents, or just chat…',

  // — ChatInput —
  attachmentFailed: 'Attachment failed.',
  voiceAttachmentLabel: 'Voice attachment ({{duration}}, {{mimeType}})',
  voiceModelUnsupported: '— current model doesn’t accept audio. Switch to a Gemini model or remove the attachment.',
  voiceModelUnsupportedShort: '— Unsupported by this model',
  removeVoiceAttachment: 'Remove voice attachment',
  cantReadAttachment: 'The current model can’t read this attachment — switch to a vision/PDF-capable model.',
  unsupportedByModel: 'Unsupported by the current model',
  removeNamed: 'Remove {{name}}',
  recordingPlaceholder: 'Recording…',
  askAnythingPlaceholder: 'Ask anything…',
  attachFileTitle: 'Attach a file (images, PDF, .txt/.md/.json/.csv)',
  attachFile: 'Attach a file',
  stopRecordingTitle: 'Stop recording',
  recordVoiceTitle: 'Record voice attachment',
  stopVoiceRecording: 'Stop voice recording',
  startVoiceRecording: 'Start voice recording',
  // Unified composer voice control (ADR 0147)
  voiceMenuLabel: 'Voice',
  voiceMenuLive: 'Live conversation',
  voiceMenuLiveHint: 'Talk back and forth, with spoken replies',
  voiceMenuAudio: 'Send audio',
  voiceMenuAudioHint: 'Record a clip to send',
  voiceLivePlaceholder: 'Listening…',
  voiceLiveStartTitle: 'Start voice conversation',
  voiceLiveStartAria: 'Start voice conversation',
  voiceLiveStopTitle: 'End voice conversation',
  voiceLiveStopAria: 'End voice conversation',
  stopGeneratingTitle: 'Stop generating (Esc)',
  stopGenerating: 'Stop generating',
  sendTitle: 'Send (Enter)',
  send: 'Send',

  // — WelcomeCard —
  welcomeHeading: 'Run workflows by name. Chat when you need to.',
  welcomeWorkflowsHeading: 'Run a workflow by name',
  welcomeBrowseAll: 'Browse all',
  welcomePickHint: 'Choosing one fills the message box — nothing sends until you press Enter.',
  welcomeIntroPrefix: 'Type ',
  welcomeIntroMid: ' to run a multi-step workflow, or ',
  welcomeIntroSuffix: ' to hand a task to one of your agents. Pick one to see it run live.',
  prefillComposer: 'Pre-fill the composer with /{{slug}}',
  workflowNotSaved: 'Workflow "{{title}}" isn’t in your saved workflows. Open the builder to create or import it.',
  runWorkflowAria: 'Put workflow {{title}} (/{{slug}}) in the message box',
  workflowNotAvailableAria: '{{title}} — workflow not available',
  notAvailable: '(not available)',
  orHandToAgent: 'Or hand it to an agent',
  handTaskToAgent: 'Hand the task to {{persona}} (@{{slug}})',
  handTaskToAgentNamed: 'Hand the task to {{persona}} — {{displayName}} (@{{slug}})',
  justChatFooter: 'Or just type a question below.',
  // — WelcomeCard · first-run getting-started (SHELL-4) —
  firstRunHeading: 'Welcome — let’s get you going.',
  firstRunEyebrow: 'Getting started',
  firstRunSkip: 'Skip intro',
  firstRunMarkerChat: 'type',
  firstRunMarkerApps: 'apps',
  firstRunStepChatTitle: 'Start a conversation',
  firstRunStepChatDesc: 'Just write in the box below.',
  firstRunStepRunTitle: 'Run a workflow',
  firstRunStepRunDesc: 'Pick a starter card, or type /.',
  firstRunStepConnectTitle: 'Connect your apps',
  firstRunStepConnectDesc: 'Link your team’s tools so agents can act.',
  firstRunStepConnectLink: 'Connect apps',
  // Generic BYOK gate for any feature embedding the chat (EmbeddedChatPanel, ADR 0073).
  embedNeedsProvider: 'Connect an AI provider to use AI here.',
  byokForbidden: 'Only a workspace owner or admin can change the AI provider.',
  byokManagedDefault: 'included',
  byokManagedDefaultHint: 'Nobody selected a provider — this is the free tier the workspace falls back to. Use Change to pick your own.',
  embedManageProvider: 'Open chat to set it up',
  embedProviderUnreachable: 'Couldn’t reach the server to check your AI provider.',
  exampleLeadTriageTitle: 'Inbound lead triage',
  exampleLeadTriageDesc: 'Enriches and scores a new inbound lead, then drafts a tailored first-touch reply for you to approve before it sends.',
  exampleLeadTriagePrompt: 'Triage the latest inbound leads',
  exampleRenewalTitle: 'Renewal & churn-risk digest',
  exampleRenewalDesc: 'Scans the book of business for renewal and churn risk and drafts a prioritized digest of the accounts that need attention.',
  exampleRenewalPrompt: 'Show the accounts most at risk of churn',
  exampleBriefingTitle: 'Daily executive briefing',
  exampleBriefingDesc: 'Pulls pipeline, usage, and open items into a single briefing so you start the day with the full picture.',
  exampleBriefingPrompt: 'Put together my executive briefing for today',
  exampleAdOptTitle: 'Ad performance optimization',
  exampleAdOptDesc: 'Reviews recent ad performance, flags creative fatigue, and proposes budget reallocations before you commit the spend.',
  exampleAdOptPrompt: 'Optimize this week’s ad spend',

  // — MessageBubble —
  copyMessage: 'Copy message',
  // ADR 0565 — selection-scoped rewrite (floating verb toolbar on a selected span)
  selRewriteToolbarAria: 'Rewrite selection',
  selRewrite: 'Rewrite',
  selShorter: 'Shorter',
  selLonger: 'Longer',
  selRewriteInstruction: 'Rewrite the quoted passage differently — keep the meaning, and reply with only the rewritten version.',
  selShorterInstruction: 'Make the quoted passage shorter — keep the meaning, and reply with only the shortened version.',
  selLongerInstruction: 'Expand the quoted passage with more detail — keep the meaning and tone, and reply with only the expanded version.',
  applyToTarget: 'Apply this response to {{target}}',
  applyToTargetShort: 'Apply',
  copied: 'Copied',
  copy: 'Copy',
  regenerateResponse: 'Try again',
  rerunPriorMessage: 'Ask the same question again',
  regenerate: 'Try again',
  goodResponse: 'Good response',
  badResponse: 'Bad response',
  noResponseSeeError: 'No response — see error below.',
  tokensIn: ' · in {{count}}',
  tokensOut: ' · out {{count}}',
  sources_one: '{{count}} source',
  sources_other: '{{count}} sources',

  // — MessageFeed —
  conversationLog: 'Conversation',
  jumpToBottom: 'Jump to latest',
  loadEarlierMessages: 'Load earlier messages',
  branchNeedsFullThread: 'Load the earlier messages to branch from a turn in this conversation.',

  // — MessageRenderer —
  codeLabel: 'code',
  copyCode: 'Copy code',
  voiceLabel: 'Voice',
  voiceWithDuration: 'Voice ({{duration}})',

  // — AgentEventCards —
  running: 'running…',
  failed: 'failed',
  toolErr_forbidden: 'This tool isn’t permitted for this agent.',
  toolErr_invalid_args: 'The tool was called with invalid arguments.',
  toolErr_forbidden_scope: 'You can view this workspace but can’t change it. Ask a workspace admin for edit access.',
  toolErrGeneric: 'The tool call failed.',
  calledBy: 'Called by {{agentId}}',
  decisionLabel: 'Decision: {{decision}}',
  rawDecision: 'Raw decision',
  verified: 'Verified',
  verificationFailed: 'Verification failed',
  revisionRequested: 'Revision requested',
  verificationTarget: '· {{target}}',

  // — AgentMentionAutocomplete —
  loadingAgents: 'Loading agents…',
  couldNotLoadAgents: 'Couldn’t load agents: {{error}}.',
  noAgentsMatch: 'No agents match <1>@{{query}}</1>.',
  agentMentionsAria: 'Agent mentions',
  agentMentionTip: 'Tip: @-mentioning an agent adds it to your active-agents lineup and switches the conversation through it.',
  slashPickerKeys: "↑↓ move · Enter or Tab to pick · Esc to close",
  noAgentsInstalled: 'No agents installed yet. Visit the <1>Agents</1> tab to install one from the registry or author your own.',

  // — BoardMentionAutocomplete —
  loadingBoards: 'Loading boards…',
  couldNotLoadBoards: 'Couldn’t load boards: {{error}}.',
  boardsLocked: 'Boards of Advisors aren’t included in your plan. <1>See bundles</1>',
  noBoardsMatch: 'No boards match <1>@@{{query}}</1>.',
  boardMentionsAria: 'Board mentions',
  boardMentionTip: 'Tip: @@-summoning a board convenes its advisors — they respond one at a time, then the chair synthesizes.',
  noBoardsInstalled: 'No boards yet. Create one in the <1>Board of Advisors</1> tab, then summon it with <3>@@</3>.',
  advisors_one: '{{count}} advisor',
  advisors_other: '{{count}} advisors',

  // — SlashAutocomplete —
  noCommandsMatch: 'No commands or workflows match <1>{{query}}</1>. Try <3>/help</3>.',
  slashCommandsAria: 'Slash commands and workflows',
  commandsSection: 'Commands',
  workflowsSection: 'Workflows',

  // — BackendStatusCard —
  backendLoading: 'Loading',
  backendWaking: 'Waking up the server',
  backendStillWaking: 'Still waking up',
  backendResting: 'The server is resting',
  backendBodyWarm: 'Reading your provider config from the host.',
  backendBodyCold: 'The Cloud Run server spins down between visits to keep hosting costs low. First load takes 10–30 seconds.',
  backendBodyColdLong: 'Cold start is taking longer than usual. Hang tight — usually under a minute.',
  backendBodyError: 'The Cloud Run server spins down between visits to keep hosting costs low. Please refresh your browser.',
  technicalDetail: 'Technical detail',
  backendUrlLabel: 'Backend URL: ',

  // — ErrorCard / errorClassify —
  openByokSettings: 'Open BYOK settings',
  errNoResponseTitle: 'No response from the model',
  errNoResponseDetail: 'The model returned an empty completion. Try rephrasing the prompt or switching to a different model.',
  errApiKeyMissingTitle: 'API key missing',
  errApiKeyMissingDetail: 'This provider requires a BYOK credential. Open the settings to add or update your key.',
  errConvStartTimeoutTitle: 'The conversation never started',
  errConvStartTimeoutDetail: 'The server could not dispatch the conversation run (still pending after 10 seconds). Check the server logs, then try again.',
  errConvGateTimeoutTitle: 'The conversation is taking too long to start',
  errConvGateTimeoutDetail: 'The conversation did not open within the expected time. Try again.',
  voiceDegradedNotice: 'Voice session degraded: the governed connection dropped, so persona, tools, and transcripts are unavailable for the rest of this call. Hang up and reconnect to restore them.',
  voiceRevokedNotice: 'Voice call ended: this call no longer has permission to act in this workspace.',
  errDailyLimitTitle: "Today's free allowance is used up",
  errDailyLimitDetail: "The free AI tier has a daily limit and you have reached it. It resets at 00:00 UTC. Add your own API key to keep going now.",
  errRateLimitedTitle: 'Rate limited',
  errRateLimitedDetail: 'The upstream provider returned 429 (Too Many Requests). Wait a few seconds and retry.',
  errProviderUnavailableTitle: 'Provider unavailable',
  errProviderUnavailableDetail: 'The upstream provider is temporarily unavailable. Retrying in a few seconds usually works.',
  errProviderTimeoutTitle: 'Provider timed out',
  errProviderTimeoutDetail: 'The request to the upstream provider exceeded the per-call timeout. Retry, or try a shorter prompt.',
  errSafetyTitle: 'Filtered by safety policy',
  errSafetyDetail: 'The provider declined the request under its content policy. Try rephrasing the prompt.',
  errInvalidOutputTitle: 'Invalid structured output',
  errInvalidOutputDetail: 'The model could not produce JSON matching the required schema. Try a different model or simplify the schema.',
  errSomethingWrongTitle: 'Something went wrong',
  errSignInTitle: 'Sign in to chat',
  errSignInDetail: 'This demo session can browse and build, but the included model needs an account. Signing in is free — or add your own API key to chat without one.',
  signInNoticeBody: 'Signing in is free. Or use your own API key — it works without an account.',
  signInNoticeOwnKey: 'Use your own key',
  errReplyLostTitle: 'The reply didn’t arrive',
  errReplyLostDetail: 'Your message was saved, but the chat lost its place in the conversation. Sending it again starts a fresh one and usually works.',
  errTechnicalDetails: 'Technical details',
  errSendAgain: 'Send again',
  errHttp429Detail: '{{provider}} returned HTTP 429. Wait a few seconds and retry.',
  errAuthFailedTitle: 'Authentication failed',
  errAuthFailedDetail: '{{provider}} rejected the credential (HTTP {{status}}). Reconfigure your BYOK key.',
  errUpstreamTitle: 'Upstream error',
  errUpstreamDetail: '{{provider}} returned HTTP {{status}}. This is usually transient — retry should clear it.',
  errRequestRejectedTitle: 'Request rejected',
  errRequestRejectedDetail: '{{provider}} returned HTTP {{status}}. {{message}}',

  // — EnvelopeInspector —
  inTokens: 'in {{count}}',
  outTokens: 'out {{count}}',
  noMetadata: '(no metadata)',
  thoughtsDetail: '{{count}} chars from {{agentId}}',
  agentFallback: 'agent',
  reasoningDetail: '{{count}} chars (carry-reasoning)',
  renderingHint: '(rendering hint)',
  confidenceDetail: 'confidence {{value}}',
  noConfidence: '(no confidence)',
  retryAttemptDetail: 'attempt {{attempt}}: {{reason}}',
  retryExhaustedDetail: '{{count}} attempts: {{reason}}',
  retryExhaustedDetailWithError: '{{count}} attempts: {{reason}} ({{error}})',
  truncationDetail: '{{reason}}',
  truncationDetailWithTokens: '{{reason}} @ {{count}} tok',
  nlCoercionDetail: '{{type}} → prose-coerced',
  nlCoercionDetailWithFallback_one: '{{type}} → prose-coerced ({{count}} fallback call)',
  nlCoercionDetailWithFallback_other: '{{type}} → prose-coerced ({{count}} fallback calls)',
  recoveryDetail: '{{path}}',
  recoveryDetailWithOffset: '{{path}} @ byte {{offset}}',
  substitutionDetail: '{{fromProvider}}/{{fromModel}} → {{toProvider}}/{{toModel}} (missing: {{missing}})',
  insufficientDetail: '{{provider}}/{{model}} missing: {{missing}}',
  toolCalledDetail: '{{tool}} (callId={{callId}}…)',
  toolReturnedError: 'error: {{code}}',
  toolReturnedOk: 'ok',
  handoffDetail: '{{from}} → {{to}}',
  handoffDetailWithReason: '{{from}} → {{to}} ({{reason}})',
  refusalDetail: '{{text}}',
  refusalDetailWithCategory: '{{category}}: {{text}}',
  noText: '(no text)',
  hideEnvelope: 'Hide envelope',
  showEnvelope: 'Show envelope',
  envelopeEventsAria: 'Envelope events',
  envelopeHelpPrefix: 'Wire-shape ',
  envelopeHelpSuffix: ' events this turn emitted.',

  // — ReasoningDisclosure —
  whyThisAnswer: 'Why this answer',

  // — ThoughtsDisclosure —
  thinkingLabel: 'Thinking',
  speakingLabel: 'Speaking',
  thoughtForSeparator: '· {{duration}}',
  thoughtFor: 'Thought for {{duration}}',
  hideReasoning: 'Hide reasoning',
  showReasoning: 'Show reasoning',
  copyReasoning: 'Copy reasoning',

  // — ArtifactPreviewModal —
  nodeLabel: 'node <1>{{nodeId}}</1>',
  closePreview: 'Close preview',
  rawOutputJson: 'Raw output JSON',

  // — HitlDecisionCard —
  justNow: 'just now',
  minutesAgo: '{{count}}m ago',
  hoursAgo: '{{count}}h ago',
  daysAgo: '{{count}}d ago',
  hitlDecisionAria: 'HITL decision: {{verb}}',
  hitlDecisionAriaWithLabel: 'HITL decision: {{verb}}, {{label}}',

  // — WorkflowRunBubble —
  steps_one: '{{count}} step',
  steps_other: '{{count}} steps',
  awaitingYourInput: 'Awaiting your input',
  runStepBarAria: '{{done}} of {{total}} steps done',
  alreadyShowingInPanel: 'Already showing in the side panel',
  openProgressPanel: 'Open the progress panel',
  showingInPanel: 'Showing in panel →',
  viewProgress: 'View progress →',
  openRunDetail: 'Open run detail',
  runRecordUnavailableTitle: 'Run record no longer available on the server',
  openWorkflowInBuilder: 'Open this workflow in the builder',
  runRecordUnavailableNote: 'Run record no longer available on the server — action links below are disabled. The decision + completion cards still render from local history.',
  workflowRunFailedSeeRuns: 'Run failed — open the run for details.',
  workflowStreamLost: 'Live updates stopped — the server could not be reached. Open the run for its current status.',
  workflowCancelFailed: 'Couldn’t cancel this run: {{message}}. It may still be running.',
  workflowsLoadFailed: 'Couldn’t load your workflows — this list may be incomplete.',
  openInBuilderLink: 'open in builder →',
  runIdShort: 'run {{id}}',

  // — WorkflowCompletionCard —
  stepCount: '{{completed}}/{{total}} steps',
  workflowFailed: 'Workflow failed',
  workflowCancelled: 'Workflow cancelled',
  workflowCompleted: 'Workflow completed',
  primaryOutputTitle: 'Primary output — declared by workflow author',
  viewLabeled: 'View {{label}}',
  viewOutput: 'View output',
  runArtifactsLabel: 'Outputs of this run',
  openInWorkbench: 'Open',
  openInRunDetail: 'Open in run detail (new tab)',
  openLabeledInRunDetail: 'Open {{label}} in run detail (new tab)',
  openRunUnavailable: 'Open run (unavailable)',
  openRun: 'Open run →',
  workflowRunAria: 'Workflow run: {{title}}',

  // — Card status labels —
  truncReasonMaxTokens: 'hit max-tokens cap',
  truncReasonLength: 'provider length cap',
  truncReasonStopSequence: 'stop sequence',
  truncatedTitle: 'Envelope output was truncated',
  truncatedBadge: 'TRUNCATED',
  truncatedTokens: '{{count}} tokens',
  partialPayloadRecovered: 'partial payload recovered',
  nlCoercionTitle: 'Natural-language to format coercion engaged',
  nlCoercionBadge: 'NL → FORMAT',
  nlCoercionPrefix: 'Coerced prose response into ',
  fallbackCalls_one: '{{count}} fallback call',
  fallbackCalls_other: '{{count}} fallback calls',
  refusalTitle: 'Model refused to answer',
  refusalBadge: 'REFUSAL',
  declined: 'declined',
  collapse: 'collapse',
  showFull: 'show full',
  recoveryTitle: 'Envelope partial recovery applied',
  recoveryBadge: 'RECOVERED',
  recoveryPrefix: 'Partial envelope salvaged at ',
  recoveryByteOffset: '@{{offset}}b',
  capInsufficientTitle: 'Model capability insufficient',
  capInsufficientBadge: 'MODEL INSUFFICIENT',
  lacks: 'lacks',
  noFallbackModel: 'no fallback model satisfies the requirements',
  chooseDifferentModel: 'Choose a different model',
  retryReasonSchema: 'schema mismatch',
  retryReasonTruncated: 'output cut off',
  retryReasonRefusal: 'model refused',
  retryAttemptTitle: 'Envelope retry attempt {{attempt}}',
  retryBadge: 'RETRY {{attempt}}',
  retryPrefix: 'Re-asked the model — ',
  hideError: 'hide error',
  showError: 'show error',
  retryExhaustedTitle: 'Envelope retry exhausted',
  retryExhaustedBadge: 'RETRY EXHAUSTED',
  gaveUpAfter_one: 'Gave up after {{formattedCount}} attempt — ',
  gaveUpAfter_other: 'Gave up after {{formattedCount}} attempts — ',
  capSubstitutionTitle: 'Model capability substitution',
  capSubstitutionBadge: 'MODEL SUBSTITUTED',
  routedAroundMissing: 'Routed around missing ',

  // — StepList / WorkflowProgressPanel —
  stepStatusStarting: 'Starting…',
  stepStatusPending: 'Pending',
  stepStatusRunning: 'Running',
  stepStatusCompleted: 'Completed',
  stepStatusFailed: 'Failed',
  stepStatusCancelled: 'Cancelled',
  hide: 'hide',
  viewOutputLower: 'view output',
  awaitingYourInputDown: 'Awaiting your input ↓',
  runningEllipsis: 'Running…',
  interruptApproval: 'approval',
  interruptClarification: 'clarification',
  interruptRefinement: 'refinement',
  interruptCancellation: 'cancellation',
  workflowProgress: 'Workflow progress',
  closeWorkflowProgress: 'Close workflow progress',
  noRunsTitle: 'No workflow runs yet',
  noRunsPrefix: 'Type ',
  noRunsSuffix: ' to dispatch one — or just ask an agent in chat.',
  runsInThisChat: 'Runs in this chat',
  stepOfTotal: 'Step {{current}} of {{total}}',
  /** Header for a run an agent tool started when the tool named no workflow. */
  /** Localized display names per workflow id. The backend supplies an English
   *  default (a tool scope carries no locale); an entry here wins for that id.
   *  A missing id simply falls back — this map never needs to be exhaustive. */
  workflowNames: {
    'openwop-app.kicktodo.challenge-factory': 'Challenge Factory',
    'openwop-app.kicktodo.replan': 'Plan revision',
    'campaign.orchestration': 'Campaign orchestration',
  },
  workflowRunGeneric: 'Workflow run',
  stepsCompleted_one: '{{count}} step completed',
  stepsCompleted_other: '{{count}} steps completed',
  cancelThisRun: 'Cancel this run',
  stepsOfTotalCompleted_one: '{{completed}} of {{total}} step completed',
  stepsOfTotalCompleted_other: '{{completed}} of {{total}} steps completed',
  awaitingSeeCard: 'Awaiting your input — see the {{kind}} card in the chat ↑',
  awaitingSeeCards_one: 'Awaiting your input on {{count}} gate — see the card in the chat ↑',
  awaitingSeeCards_other: 'Awaiting your input on {{count}} gates — see the cards in the chat ↑',

  // — defaultCards —
  pleaseApprove: 'Please approve to continue.',
  approvalRequired: 'Approval required',
  view: 'view',
  pickThis: 'Pick this',
  commentOptional: 'Comment (optional)',
  visibleInAuditTrail: 'Visible in audit trail',
  clarificationNeeded: 'Clarification needed',
  connectionRequiredTitle: 'Connection needed',
  connectionRequiredPrompt: 'This step needs a {{label}} connection to continue.',
  connectionRequiredFallbackLabel: 'connection',
  connectionRequiredConnect: 'Connect {{label}}',
  connectionRequiredConnecting: 'Opening consent…',
  connectionRequiredContinue: "I've connected — continue",
  connectionRequiredSkip: 'Skip this step',
  connectionRequiredManage: 'Set up in Access & connections',
  connectionRequiredCapabilityHint: 'Connect a {{label}} app under Access & connections, then continue.',
  connectionRequiredConnectError: 'Could not start the consent flow — set it up under Access & connections.',
  pleaseClarify: 'Please clarify.',
  submit: 'Submit',
  refinementRequested: 'Refinement requested',
  submitRefinement: 'Submit refinement',
  cancellationRequested: 'Cancellation requested',
  cancellationRequestedBody: 'A cancellation has been requested.',
  confirmCancel: 'Confirm cancel',
  decline: 'Decline',
  cardLabelApproval: 'Approval',
  cardLabelClarification: 'Clarification',
  cardLabelRefinement: 'Refinement',
  cardLabelCancellation: 'Cancellation',

  // — defaultCommands —
  cmdNewDescription: 'Start a new chat session (wipes the current thread)',
  cmdHelpDescription: 'List available slash commands',
  cmdHelpAliases: '(aliases: {{aliases}})',
  cmdHelpHeader: 'Available commands:\n{{lines}}',
  cmdStopDescription: 'Cancel the in-flight turn (same as Esc)',

  // — CardHost —
  cardCrashedPrefix: 'Card ',
  cardCrashedSuffix: ' crashed:',
  noCardRegisteredPrefix: 'No card registered for ',
  noCardRegisteredSuffix: '. Register one with ',

  // — LeftRail —
  tabConversations: 'Conversations',
  tabWorkflow: 'Workflow',
  tabReviews: 'Reviews',
  reviewsBadgeA11y: 'reviews pending: {{count}}',
  chatToolsAside: 'Chat tools',
  chatToolTabs: 'Chat tool tabs',
  tabCloseTitle: '{{label}} (click to close)',
  tabSwitchTitle: 'Switch to {{label}}',

  // — ConversationsRail —
  conversationsHeading: 'Conversations',
  openWorkspaceAssistant: 'Open the Workspace assistant',
  workspace: 'Workspace',
  closeConversations: 'Close conversations',
  closeReviews: 'Close reviews',
  inThisConversation: 'In this conversation',
  membersToggleAria_one: 'Members ({{count}}) — show who’s here',
  membersToggleAria_other: 'Members ({{count}}) — show who’s here',
  closeMembers: 'Close members',
  conversationMentionTipPrefix: 'Type ',
  conversationMentionTipMid: ' to add an agent, or ',
  conversationMentionTipSuffix: ' to convene a council.',
  searchConversationsPlaceholder: 'Search conversations…',
  searchConversations: 'Search conversations',
  messageMatches: 'Message matches',
  messageMatchesCount_one: '{{count}} message match',
  messageMatchesCount_other: '{{count}} message matches',
  searchingMessages: 'Searching messages…',
  messageSearchFailed: "Couldn't search messages. Title matches are still shown.",
  searchNoMatches: 'No conversations or messages match.',
  searchTruncated: 'Showing matches from the most recent messages. Add a word to narrow it.',
  untitledConversation: 'Untitled conversation',
  conversationRenamed: 'Conversation renamed to {{title}}',
  noConversationsYet: 'No conversations yet',
  sectionNeedsYou: 'Needs you',
  sectionAgents: 'Agents',
  sectionChannels: 'Channels',
  sectionGroups: 'Groups',
  sectionWorkspace: 'Workspace',
  // ADR 0154 — channel presence, rendered as chat chrome (ported from the
  // retired standalone channels view).
  reconnecting: 'Reconnecting…',
  presentCount_one: '{{count}} present',
  presentCount_other: '{{count}} present',
  typingCount_one: '{{count}} typing…',
  typingCount_other: '{{count}} typing…',
  channelPostError: 'Couldn’t send your message. Try again.',
  // ADR 0154 Phase 2 — channel management chrome
  newChannelCta: 'New channel',
  channelsEmptyBody: 'Shared rooms for your team and AI agents — @mention an agent to put it to work.',
  createChannelTitle: 'New channel',
  channelNameLabel: 'Channel name',
  channelNamePlaceholder: 'e.g. product',
  visibilityLabel: 'Visibility',
  // ADR 0854 D1 — approval handoffs
  reviewHandoffAsk: 'Ask someone else to decide',
  reviewHandoffTo: 'Hand to',
  reviewHandoffPick: 'Choose a person',
  reviewHandoffIntent: 'What they need to know (required)',
  reviewHandoffQuestions: 'Still open (one per line)',
  reviewHandoffHint: "Only someone who can already approve this can receive it; handing it off doesn't give anyone new permissions.",
  reviewHandoffSend: 'Hand off',
  reviewHandoffNotApprover: "That person isn't an approver for this request.",
  reviewHandoffFailed: "The handoff didn't go through. Nothing changed; try again.",
  reviewHandoffPeopleFailed: "Couldn't load the people in this workspace, so there's no one to pick. Close this and try again.",
  reviewHandoffNoteTitle: 'Handed to you with this note',
  // ADR 0853 D2 — record a decision
  recordAsDecision: 'Record as decision',
  recordDecisionTitle: 'Record a decision',
  recordDecisionLede: "This saves a decision record linked to this message and proposes adding it to the project's team ledger. The ledger owner accepts it.",
  recordDecisionLedeOwner: "This saves a decision record linked to this message and adds it to the project's team ledger.",
  recordDecisionTitleLabel: 'Decision (short title)',
  recordDecisionTextLabel: 'What was decided',
  recordDecisionRationaleLabel: 'Why (optional)',
  recordDecisionAcceptNow: 'Accept it into the ledger now',
  recordDecisionSubmit: 'Record',
  recordDecisionFailed: 'The decision could not be recorded. Nothing was saved; try again.',
  decisionRecordedProposed: 'Decision recorded and proposed to the ledger owner',
  decisionRecordedAccepted: 'Decision recorded and added to the ledger',
  // ADR 0852 D5 — the channel's bound project and its ledger
  channelProjectLabel: 'Project',
  channelProjectNone: 'No project',
  channelProjectUnavailable: 'A project you cannot see',
  channelProjectUnknown: 'The bound project (the project list couldn’t be loaded)',
  channelProjectsLoadFailed: 'Couldn’t load the project list, so the bound project can’t be shown or changed right now. Close and reopen to retry.',
  channelProjectHint: "Agents in this channel read the project's team ledger. Only a project the whole workspace can see can be chosen.",
  channelLedgerTitle: 'Team ledger',
  channelLedgerProject: 'Project: {{name}}',
  channelLedgerVersion: 'Version {{version}}',
  channelLedgerOpenQuestions_one: '{{count}} open question',
  channelLedgerOpenQuestions_other: '{{count}} open questions',
  channelLedgerPending_one: '{{count}} change waiting',
  channelLedgerPending_other: '{{count}} changes waiting',
  channelLedgerNone: 'This project has no team ledger yet.',
  channelLedgerOpen: 'Open the ledger',
  channelLedgerFailed: 'The team ledger could not be loaded.',
  visibilityPublic: 'Public',
  visibilityPrivate: 'Private',
  visibilityPublicHint: 'Any workspace member can find and join.',
  visibilityPrivateHint: 'Only invited members can see it.',
  createChannelError: 'Couldn’t create the channel. Try again.',
  renameChannelLabel: 'Name',
  membersLabel: 'Members',
  addMemberSubmit: 'Add',
  removeMemberAria: 'Remove {{member}}',
  archiveChannelCta: 'Archive channel',
  archiveChannelConfirm: 'Archive this channel? Members lose access from their rail.',
  manageError: 'Something went wrong. Try again.',
  roleOwner: 'owner',
  roleMember: 'member',
  channelSettingsAria: 'Channel details',
  // ADR 0154 FU-4 — public-channel discovery + self-join
  browseChannelsTitle: 'Browse channels',
  browseChannelsCta: 'Browse channels',
  noPublicChannels: 'No channels to browse yet.',
  openChannelCta: 'Open',
  joinChannelCta: 'Join',
  joinChannelError: 'Couldn’t join the channel. Try again.',

  // ADR 0192 — channels UX & identity parity
  channelComposerPlaceholder: 'Message #{{name}}',
  channelReadOnlyComposer: 'Only the channel’s managers can post here.',
  channelReadOnlyRole: 'Your workspace role can read channels but not post. Ask a workspace admin for Editor access.',
  channelArchivedComposer: 'This channel is archived. Its history stays readable; nobody can post.',
  channelWriteForbidden: 'Your workspace role is read-only, so you can’t change channels. Ask a workspace admin for Editor access.',
  channelMemberNotInWorkspace: 'That person is no longer a member of this workspace, so they can’t join this channel.',
  postingPolicyLabel: 'Who can post',
  postingPolicyEveryone: 'Everyone in the channel',
  postingPolicyManagers: 'Managers only (announcements)',
  postingPolicyHint: 'Members can still read and react. Agent members still reply to a manager’s post.',
  channelAgentMemberDescription: 'Agent in this channel',
  channelEmptyTitle: 'This is the very beginning of {{name}}',
  channelEmptyNoDescription: 'Add a description so your team knows what belongs here.',
  channelEmptyMemberHint: 'Say hello, or @mention an agent to put it to work.',
  channelEmptyMemberHintNoAgents: 'Say hello — messages here go to the channel\u2019s members.',
  channelEmptyAddPeople: 'Add people',
  channelEmptyAddAgent: 'Add an agent',
  channelEmptySetDescription: 'Set a description',
  channelRosterHeading: 'In this channel',
  channelMembersAria_one: '{{count}} member — open channel details',
  channelMembersAria_other: '{{count}} members — open channel details',
  channelDetailsTitle: 'Channel details',
  channelDescriptionLabel: 'Description',
  channelDescriptionPlaceholder: 'What is this channel for?',
  channelNamePreview: 'Will be created as {{name}}',
  channelMembersPickerLabel: 'Invite people',
  channelAgentsPickerLabel: 'Add agents',
  channelPickerSearchPlaceholder: 'Search\u2026',
  channelPickerEmpty: 'No matches.',
  channelPickerError: 'Couldn\u2019t load the list. Close and reopen to retry.',
  channelInviteCap: 'You can invite up to 50 people and 50 agents at creation — remove some to continue.',

  // ADR 0195 — message affordances
  messageDeleted: 'Message deleted',
  messageRemovedBy: 'Removed by {{name}}',
  messageRemovedByModerator: 'a moderator',
  spokenTurn: 'Spoken',
  twinRecallUsed: 'Drew on your shared memories',
  // ADR 0665 D4 — the advisor produced no output. Says what happened, not an
  // apology: a council reader needs to know the seat was silent, because the
  // chair's synthesis runs over the same transcript.
  noContributionNotice: 'No response from this advisor',
  contextDegradedNotice: 'This reply was composed with reduced context — unavailable: {{blocks}}.',
  contextBudgetedNotice_one: "The assistant saw only the most recent messages; {{count}} earlier one wasn't sent.",
  contextBudgetedNotice_other: "The assistant saw only the most recent messages; {{count}} earlier ones weren't sent.",
  voiceReducedContext: 'Reduced context',
  voiceReducedContextTitle: 'This voice session started without: {{blocks}}',
  voiceContextCollapsed: 'Voice started without your assistant’s context — replies may be generic.',
  voiceCtxBlock_identity: 'your name',
  voiceCtxBlock_persona: 'the agent persona',
  voiceCtxBlock_agent_knowledge: 'the agent’s knowledge',
  voiceCtxBlock_owner_knowledge: 'workspace knowledge',
  voiceCtxBlock_preamble: 'the work snapshot',
  voiceCtxBlock_whole: 'all context',
  // M2 — every ledger value the backend can push MUST have a key here; the
  // lookup is composed dynamically, so `check-i18n.mjs` cannot see these.
  voiceCtxBlock_twin_borrowed_recall: 'your shared memories',
  voiceCtxBlock_twin_borrowed_recall_partial: 'part of your shared memories (a source failed to load)',
  voiceCtxBlock_board_context: 'the board’s plans',
  voiceCtxBlock_board_context_partial: 'part of the board’s plans',
  voiceCtxBlock_other: 'other context',
  voiceChat: 'Voice chat',
  voiceEndedOnSwitch: 'Voice conversation ended — you switched conversations.',
  voiceCallEndedRemote: 'The voice call ended.',
  voiceSttUnavailable: 'Voice transcription isn’t available with this workspace’s voice setup. Talk to one agent with real-time voice, or ask an admin to switch the real-time voice provider to Gemini Live.',
  voiceTtsUnavailable: 'Spoken replies aren’t set up for this workspace. Add a text-to-speech provider in voice settings to hear replies aloud.',
  messageEdited: '(edited)',
  editMessageAria: 'Edit message',
  askForSource: "Ask for a source",
  askSourceDraft: "@{{handle}} what’s your source for: \"{{quote}}\"?",
  deleteMessageAria: 'Delete message',
  reactWithAria: 'React with {{emoji}}',
  reactionChipAria: '{{emoji}} — {{count}}. Toggle your reaction.',
  reactionsGroupAria: 'Reactions',
  deleteMessageConfirmTitle: 'Delete this message?',
  deleteMessageConfirmBody: 'It will show as deleted for everyone in the conversation.',
  reactionFailed: 'Couldn’t update the reaction. Try again.',
  editMessageFailed: 'Couldn’t save the edit. Try again.',
  deleteMessageFailed: 'Couldn’t delete the message. Try again.',

  // ADR 0202 — agent-native channels
  botBadge: 'Agent',
  responsePolicyLabel: 'Replies',
  responsePolicyAll: 'To every message',
  responsePolicyMention: 'When @mentioned',
  responsePolicyAria: 'Reply policy for {{agent}}',
  // ADR 0202 D3 — scheduled agent posts
  schedPostsTitle: 'Scheduled posts',
  schedPostsHint: 'Have an agent post to this channel on a recurring schedule — a daily standup, a weekly digest.',
  schedPostsEmpty: 'No scheduled posts yet — create one below.',
  schedLoadError: 'Couldn’t load scheduled posts. Try again.',
  schedAgentRemoved: 'Agent is no longer a member',
  schedConfirmDelete: 'Delete this scheduled post?',
  schedTzHint: 'Times are in your timezone ({{tz}}).',
  schedScheduled: 'Scheduled.',
  schedAgentLabel: 'Agent',
  schedAgentPlaceholder: 'Choose an agent…',
  schedPromptLabel: 'What should it post?',
  schedPromptPlaceholder: 'e.g. Post a short standup summary for the team',
  schedCadenceLabel: 'How often',
  schedTimeLabel: 'Time',
  schedDayLabel: 'Day',
  schedFreq_daily: 'Every day',
  schedFreq_weekdays: 'Weekdays',
  schedFreq_weekly: 'Weekly',
  schedFreq_hourly: 'Every hour',
  schedLabelHourly: 'Every hour',
  schedLabelDaily: 'Every day at {{time}}',
  schedLabelWeekdays: 'Weekdays at {{time}}',
  schedLabelWeekly: '{{day}} at {{time}}',
  schedCreateCta: 'Schedule post',
  schedNextRun: 'Next: {{when}}',
  schedPaused: 'Paused',
  schedLastRunNotPosted: 'Last run could not post here. Check the agent is still a member and its creator can still post.',
  schedLastRunFailed: 'Last run failed: {{reason}}',
  schedPauseCta: 'Pause',
  schedResumeCta: 'Resume',
  schedNoAgents: 'Add an agent member to schedule a recurring post.',
  schedDeleteAria: 'Remove scheduled post',
  schedError: 'Couldn’t update scheduled posts. Try again.',
  newMessagesLabel: 'New messages',
  summarizeWhatIMissed: 'Summarize what I missed',
  summarizingRecap: 'Summarizing what you missed…',
  recapPrefix: 'Recap of what you missed:\n\n{{summary}}',
  recapFailed: 'Couldn’t generate a recap. Try again.',
  recapTimedOut: 'The recap is taking too long — try again.',
  channelPickerSelectedCount_one: '{{count}} selected',
  channelPickerSelectedCount_other: '{{count}} selected',
  manageMembersCta: 'Manage members',
  leaveChannelCta: 'Leave channel',
  leaveChannelConfirmTitle: 'Leave this channel?',
  leaveChannelConfirmBody: 'You\u2019ll lose access until someone adds you back (or you rejoin a public channel).',
  leaveChannelError: 'Couldn\u2019t leave the channel. Try again.',
  leftChannelToast: 'Left #{{name}}',
  rosterYou: 'You',
  muteChannel: 'Mute channel',
  unmuteChannel: 'Unmute channel',
  mutedLabel: 'Muted',
  unreadCountAria_one: '{{count}} unread message',
  unreadCountAria_other: '{{count}} unread messages',
  mentionCountAria_one: '{{count}} mention',
  mentionCountAria_other: '{{count}} mentions',
  channelMemberCount_one: '{{count}} member',
  channelMemberCount_other: '{{count}} members',
  channelAgentCount_one: '{{count}} agent',
  channelAgentCount_other: '{{count}} agents',
  channelLastActivity: 'Active {{time}}',
  channelPostTooLarge: 'That message is too large to send.',
  messageTimestampAria: 'Sent at {{time}}',
  // ADR 0154 Phase 4 — agents in channels
  roleAgent: 'agent',
  tryAgain: 'Try again',
  switchToPersona: 'Switch to {{persona}}',
  removeFromConversation: 'Remove {{persona}} from this conversation',
  removeFromConversationTitle: 'Remove from this conversation',
  renameConversation: 'Rename conversation',
  unread: 'Unread',
  deleteConversationTitle: 'Delete this conversation?',
  deleteConversationBody: 'The conversation and all messages are removed permanently.',
  renameConversationFailed: 'Couldn’t rename the conversation. Try again.',
  deleteConversationFailed: 'Couldn’t delete the conversation. Try again.',
  deleteConversation: 'Delete conversation',
  participants_one: '{{count}} participant',
  participants_other: '{{count}} participants',
  messages_one: '{{count}} message',
  messages_other: '{{count}} messages',

  // — A2uiSurfaceCard —
  a2uiUnsafe: 'This interactive surface could not be rendered safely: {{reason}}.',
  a2uiVersionMismatch: 'This surface targets A2UI catalog v{{surfaceVersion}}; this host renders v{{hostVersion}}. It was not rendered.',
  a2uiFillRequired: 'Fill the required fields to continue.',

  // — activeAgents —
  defaultAssistantPersona: 'OpenWOP Assistant',

  // — boardroomCadence —
  boardSynthesisPrompt: "As chair, synthesize the board’s perspectives: name where the advisors AGREE, name each DISSENT and who holds it, then give a clear recommendation and what would change it.",
  // `COLWF-3` — the PROJECT lane's synthesis prompt. Same job, no board vocabulary.
  projectSynthesisPrompt: 'As lead, synthesize the team’s input: name where the members AGREE, name each DISAGREEMENT and who holds it, then give a clear recommendation and what would change it.',
  orchestratedTurnLabel: "Board orchestration — not typed by you",
  boardCadenceHalted: "The boardroom stopped after this turn. {{remaining}} advisor(s) did not speak{{synthesis}}. Nothing was synthesized from a partial board — re-summon @@{{handle}} to run it again.",
  boardCadenceHaltedSynthesis: ", and the chair’s synthesis did not run",
  boardCadenceHaltedNoSynthesis: "",
  boardContextStale: "Heads up: this boardroom’s saved record of the plans it was given couldn’t be refreshed, so it is out of date. Your advisors are still grounded in the plans you can read, checked fresh on every turn.",
  // L3 — the PROJECT-team lane has its own surface and its own exit. It used to
  // borrow the boardroom sentence with an empty handle, so a failed project
  // convene said "The boardroom stopped … re-summon @@" — the wrong surface and
  // a dangling token, which is the half-disclosure ADV-UX-4 exists to prevent.
  projectCadenceHalted: "Your project team stopped after this turn. {{remaining}} teammate(s) did not speak{{synthesis}}. Nothing was synthesized from a partial team — send @@ again to run it.",
  boardAdvisorPrompt: '{{persona}}, your perspective?',

  // — useChatSession / persistence / sessions —
  stoppedByUser: 'Stopped by user.',
  // ADR 0173 — non-blocking soft-warning shown after a successful turn that
  // crossed the org's BYOK daily spend threshold.
  byokBudgetWarning: 'Approaching this workspace’s daily spend limit ({{usedPct}}% of {{cap}} tokens).',
  deepRunBudgetExceeded: 'Answered directly — this conversation has used its {{limit}} deep investigations for the hour.',
  replyFailed: 'The reply could not be generated.',
  replyTimedOut: 'The reply timed out. Please try again.',
  savedChat: 'Saved chat',
  untitled: 'Untitled',
  workflowNeedsInputs_one: 'Workflow "{{name}}" needs {{fields}} before it can run. Type it after the command (/{{slug}} your value), or open the workflow from the dashboard.',
  workflowNeedsInputs_other: 'Workflow "{{name}}" needs {{fields}} before it can run. Type /{{slug}} and fill them in the form above the message box, or open the workflow from the dashboard.',
  slashInputsLabel: 'Inputs for /{{slug}}',
  handoffHint: "Hands this conversation to {{name}}: replies come from {{name}} until you switch agents.",
  attachUnreadableNote: "The model answering this message can’t read one of these attachments.",
  handoffIgnoredWithFiles: "Files are attached, so @{{name}} won’t switch agents; it will be sent as text.",
  slashUsualDuration: 'Usually takes {{duration}}',
  slashUsualCost: 'about {{cost}} a run (median of {{count}})',
  workflowDeleted: 'Workflow "{{name}}" was deleted. Pick another from the dashboard or remove the mention.',

  // — useAudioRecorder —
  micAccessDenied: 'Microphone access denied or unavailable: {{reason}}',
  unknownRecorderError: 'unknown recorder error',

  // — agentMentions / workflowMentions / availableTools / composeProviderMessages —
  agentFallbackDescription: '{{modelClass}} agent from {{packName}}',
  exampleWorkflowName: 'Uppercase',
  exampleWorkflowDescription: 'Uppercases the `text` field. Input: { text: string }. Returns the uppercased text.',
  workflowNodeSummary_one: '{{count}} node: {{steps}}',
  workflowNodeSummary_other: '{{count}} nodes: {{steps}}',
  workflowNodeCount_one: '{{count}} node',
  workflowNodeCount_other: '{{count}} nodes',
  mentionToolDescription: 'Mention: @{{slug}}. {{description}}',
  a2uiRejectMissingDiscriminator: 'a component is missing a string `component` discriminator',
  a2uiRejectUnknownComponent: 'component "{{component}}" is not in the host A2UI catalog (v{{version}})',
  a2uiRejectRequiresText: '"{{component}}" requires a string `text`',
  a2uiRejectRequiresIdLabel: '"{{component}}" requires string `id` + `label`',
  a2uiRejectSelectOptions: '"field.select" requires a non-empty `options` array',
  a2uiRejectSelectOptionShape: '"field.select" options need string `value` + `label`',
  a2uiRejectButtonIdLabel: '"action.button" requires string `id` + `label`',
  a2uiRejectButtonTarget: '"action.button" requires `action.target` of "resume"',
  a2uiRejectPayloadNotObject: 'surface payload is not an object',
  a2uiRejectMissingCatalogVersion: 'payload is missing `catalogVersion`',
  a2uiRejectMissingSurface: 'payload is missing a `surface` object',
  a2uiRejectMissingComponents: 'surface is missing a `components` array',
  // — A2UI v0.9 (RFC 0209, ADR 0749) —
  a2uiV09Pending: 'The agent is still building this form.',
  a2uiV09Closed: 'The agent closed this form.',
  a2uiV09From: 'From {{agent}}',
  a2uiV09DateLabel: 'Date',
  a2uiV09ChoosePlaceholder: 'Choose one',
  a2uiV09ActionFallback: 'Continue',
  a2uiV09RejectComponent: 'the component "{{component}}" is not in the A2UI v0.9 profile this host renders',
  a2uiV09RejectComponentShape: 'the "{{component}}" component "{{id}}" has a property or value the A2UI v0.9 profile does not allow',
  a2uiV09RejectMessage: 'message {{index}} is not a valid A2UI v0.9 message for this surface',
  a2uiV09RejectCatalog: 'the surface names a catalog this host does not pin',
  a2uiV09RejectMixedSurfaces: 'the updates belong to more than one surface',
  a2uiV09RejectTooLarge: 'the surface expands to more elements than this host renders',

  // — Unified review inbox (ADR 0068 / 0070) —
  reviewSourceInflight: 'In-flight',
  reviewSourceProposal: 'Proposal',
  reviewRiskLabel: '{{level}} risk',
  reviewFromWorkflow: 'from {{workflow}}',
  reviewPreview: 'Preview',
  underReview: 'Under review',
  sendReviewHeading: 'Message to send',
  sendReviewTo: 'To',
  sendReviewSubject: 'Subject',
  cuGateHeading: 'Browser agent action',
  cuWantsTo: 'The browser agent wants to {{action}} on',
  cuTargetLabel: 'Page:',
  cuActionSubmit: 'submit a form',
  cuActionDownload: 'download a file',
  cuActionNavigate: 'open a new site',
  cuActionOther: 'take a {{kind}} action',
  cuProviderNoteHeading: 'Unverified: reported by the browser agent from the page',
  cuOpenerOnly: 'Only the person who started this browser session can approve or reject this action. Rejecting ends the session.',
  // ADR 0995 D2 — the code-exec approval gate's evidence block.
  codeExecGateHeading: 'Code to run',
  codeExecGateCodeLabel: '{{language}} code to run',
  codeExecGateStdin: 'Input:',
  codeExecGateStdinTruncated: 'Input (first {{shown}} of {{total}} characters):',
  codeExecGateNote: 'This exact code runs in an isolated sandbox if you approve. Read it before approving.',
  // ADR 0995 D10 — the code.execution-result renderer.
  codeResultOk: 'Exit 0',
  codeResultFailed: 'Exit {{code}}',
  codeResultTimedOut: 'Timed out',
  codeResultStdout: 'Output',
  codeResultStderr: 'Errors',
  codeResultEmpty: 'No output.',
  codeResultUnreadable: 'This code result could not be read.',
  viewFull: 'View full',
  previewLoading: 'Loading preview…',
  assetPreviewLabel: 'Preview: {{title}}',
  assetPreviewNone: 'No preview available for this item.',
  gateEvidenceUnreadable: 'We couldn’t load what you’re being asked to approve. Don’t decide from this card — retry, or open the run.',
  gateEvidenceNone: 'Nothing was captured for this gate, so there is no preview of what you’re approving. Open the run before deciding.',
  gateEvidenceUnrenderable: 'This gate carries something this card can’t display. Open the run to see what you’re approving before deciding.',
  outputsNotOnThisDevice: 'This workflow’s graph isn’t cached on this device, so its outputs can’t be listed here. Open the run to see them.',
  outputsNotOnThisDeviceNoRun: 'This workflow’s graph isn’t cached on this device, so its outputs can’t be listed here.',
  gateEvidenceMore_one: '+{{count}} more item under review.',
  gateEvidenceMore_other: '+{{count}} more items under review.',
  assetPreviewArtifactRef: 'Linked artifact: {{id}}',
  // ADR 0459 grade-fix — the typed plan-revision approval card (humanized, no raw ids).
  lessonBatchDayTitle: 'Day {{day}}: {{title}}',
  lessonBatchPlanRow: 'Plan: {{instruction}}',
  lessonBatchPlanWhy: 'Why: {{why}}',
  lessonBatchCites: 'Cites evidence: {{ids}}',
  lessonBatchCitesNone: 'Cites no evidence (stays within the plan day).',
  lessonBatchImageAlt: 'Generated image for day {{day}}',
  lessonBatchImageLoading: 'Loading the generated image…',
  lessonBatchUnreadable: 'The lessons in this checkpoint could not be read. Do not approve it; reject it or check the run.',
  challengePlanUnreadable: 'The outline could not be read. Do not approve it; reject it or check the run.',
  challengePlanShape: 'For {{audience}} · {{days}} days · about {{minutes}} minutes a day',
  challengePlanOutcomesHeading: 'Outcomes',
  challengePlanDayTitle: 'Day {{day}}: {{title}}',
  challengePlanAlternativesHeading: 'Alternatives',
  challengePlanAlternative: '{{title}}: {{instruction}}',
  readableJsonMore: '{{count}} more not shown. Open Raw to see everything.',
  readableJsonAwaitingReview: 'Submitted and awaiting review. Not published yet.',
  readableJsonApprovalState: 'Approval request: {{state}}',
  readableJsonUnverifiedNote: 'Saved as plain data, so its format was not checked. It is shown in its usual layout because it has the expected shape.',
  readableJsonAllFields: 'All saved fields',
  lessonBatchEmpty: 'This checkpoint has no lessons to show.',
  planRevisionChangesHeading: 'Proposed changes',
  planRevisionNoChanges: 'No changes to your plan.',
  planRevisionRawSummary: 'Show the raw commands',
  planRevisionLaneSchedule: 'Change your preferred time of day.',
  planRevisionLaneSubstitute: 'Swap an activity for a publisher-approved alternative.',
  planRevisionLaneRecovery: 'Collapse missed activities into a recovery day.',
  planRevisionLaneOther: 'A plan adjustment.',
  assetPreviewMediaAlt: 'Generated media under review',
  reviewRequestedBy: 'Requested by',
  reviewRequested: 'Requested',
  reviewDue: 'Due',
  reviewNoteLabel: 'Reviewer note',
  reviewNotePlaceholder: 'Add a note (optional)…',
  reviewCardLabel: 'Review: {{summary}}',
  reviewQuorumApproved: '{{approvals}} of {{required}} approved',
  reviewQuorumRejected: '{{count}} rejected',
  reviewQuorumAria: 'Quorum: {{approvals}} of {{required}} approved',
  composedSectionAria: 'Proposed workflow',
  composedKindLabel: 'Proposed workflow',
  composedPreviewUnavailable: 'Live preview unavailable on this connection — open the draft in the builder to verify and approve it there.',
  composedMoreSteps_one: '+{{count}} more step',
  reviewRiskLow: 'low',
  reviewRiskMedium: 'medium',
  reviewRiskHigh: 'high',
  reviewRiskCritical: 'critical',
  composedStaleError: 'The draft changed since you reviewed it — the card has refreshed; review it and approve again.',
  composedAgentNote: "Agent's description",
  composedEvidence: 'Validated · {{nodes}} nodes · {{edges}} edges',
  composedEdited: 'Edited since proposed — approving runs the current version',
  composedMoreSteps: '+{{count}} more steps',
  composedInputs: 'Run inputs',
  composedOpenBuilder: 'Open in builder',
  composedExpires: 'Expires {{date}}',
  composedExpiredNotice: 'Expired — ask the agent to propose again',
  composedApproveRun: 'Approve & run',
  composedReject: 'Reject',
  composedRolePure: 'pure',
  composedRoleRead: 'read',
  composedRoleGate: 'gate',
  composedRoleAction: 'action',
  composedRoleSideEffect: 'side effect',
  composedRoleStreaming: 'streaming',
  composedRoleUnclassified: 'unclassified',
  reviewInboxLabel: 'Review inbox',
  reviewInboxLoading: 'Loading reviews…',
  reviewInboxErrorTitle: 'Couldn’t load the review inbox',
  reviewInboxEmptyTitle: 'No pending reviews',
  reviewInboxEmptyBody: 'Approvals and in-flight workflow gates that need a human will appear here.',
  reviewDecisionRecorded: 'Decision recorded.',
  reviewRespondOnRun: 'Answer on the run page',
  reviewAnswerNeedsAdmin: 'This request needs an answer this card can’t collect. A workspace admin can answer it from the run.',
  reviewRejectConfirmTitle: 'Reject this proposal?',
  reviewRejectConfirmBody: 'Rejecting closes this request. It cannot be un-rejected; the requester would have to ask again.',
  reviewNotAnApprover: 'Only the approvers named on this request can decide it.',
  reviewResolvedElsewhere: 'This review was just resolved elsewhere.',
  reviewOpenArtifact: 'Open artifact',

  // — Chat artifact workbench (ADR 0069) —
  artifactWorkbenchFallbackTitle: 'Artifact',
  artifactViewsLabel: 'Artifact views',
  artifactTabPreview: 'Preview',
  artifactTabRaw: 'Raw',
  artifactTabRevisions: 'Revisions',
  artifactTabDiff: 'Diff',
  artifactTabProvenance: 'Provenance',
  artifactLoading: 'Loading artifact…',
  artifactLoadError: 'Could not load this artifact. Please try again.',
  artifactReactUnsupported: "Interactive React artifacts aren't rendered in this host — showing the source.",
  artifactSandboxedBadge: 'Sandboxed',
  artifactSandboxedHint: 'Untrusted content. Scripts stay off until you run them; running scripts can send this content to other sites.',
  artifactScriptsOff: 'Scripts are off, so this static preview can\'t be scrolled or clicked. Running its scripts lets it interact, and lets it send this content, and anything you type into it, to other sites.',
  artifactScriptsRunning: 'Scripts are running. They can send this content, and anything you type into it, to other sites.',
  artifactStaticComplete: 'This content has no scripts or links, so this static preview shows all of it.',
  artifactRunScripts: 'Run scripts',
  artifactStopScripts: 'Stop scripts',
  artifactTextAlternative: 'Text of this preview',
  chatTurnArtifactsLabel: 'Artifacts from this reply',
  chatTurnArtifactsFailed: 'Couldn\'t load the artifacts from this reply. They are also in your Library.',
  chatTurnArtifactsRetry: 'Try again',
  // ADR 0153 Phase 1 — slides canvas
  slidesInvalid: 'This slide deck could not be rendered (invalid deck data).',
  slidesDeckLabel: 'Slide deck',
  slidesNotesLabel: 'Notes:',
  // ADR 0153 Phase 2 — app-builder canvas
  appBuilderInvalid: 'This app design could not be rendered (invalid design data).',
  appBuilderScreensLabel: 'App screens',
  appBuilderInitial: 'Home',
  appBuilderNoScreens: 'This app design has no screens yet.',
  appBuilderHiddenMobile: 'Hidden on mobile',
  appBuilderHiddenDesktop: 'Hidden on desktop',
  appBuilderHiddenNode: "Hidden",
  appBuilderModalClose: "Close dialog",
  appFacetInvalid: "This artifact payload could not be read.",
  appBuilderRating: '{{v}} of {{max}} stars',
  appBuilderSlider: '{{v}} (range {{min}}–{{max}})',
  appBuilderFlowsLabel: 'Navigation',
  appBuilderOpenEditor: 'Open in editor',
  slidesOpenEditor: 'Open in editor',
  canvasOpenEditor: 'Open in editor',
  // ADR 0153 Phase 3 — campaign-studio canvas
  campaignInvalid: 'This campaign could not be rendered (invalid campaign data).',
  campaignAudience: 'Audience:',
  campaignChannels: 'Placements',
  campaignBudget: 'Budget:',
  campaignBudgetRollup: 'Channel budgets total {{sum}}.',
  campaignFunnel: 'Funnel',
  campaignAssets: 'Content',
  // ADR 0153 Phase 4 — drawings canvas
  drawingInvalid: 'This drawing could not be rendered (invalid drawing data).',
  drawingLabel: 'Drawing',
  // ADR 0153 Phase 4 — cad canvas
  cadInvalid: 'This model could not be rendered (invalid model data).',
  cadLabel: 'CAD model',
  cadView2d: '2D',
  cadView3d: '3D',
  cadViewLabel: 'View mode',
  cad3dKeyboardHint: '3D model viewer. Drag or use the arrow keys to orbit.',
  cadOrbitHint: 'drag or arrow keys to orbit',
  cadSolids: '{{n}} solids',
  artifactNoPreview: 'No preview available.',
  artifactOpenFile: 'Open file: {{name}}',
  artifactNoContent: '(no content)',
  artifactPickTwoTitle: 'Pick two revisions to compare',
  artifactPickTwoBody: 'Select two revisions in the Revisions tab to see what changed.',
  artifactComputingDiff: 'Computing diff…',
  artifactDiffNoStructural: 'No structural changes.',
  artifactDiffNoChanges: 'No changes between these revisions.',
  artifactDiffPath: 'Path',
  artifactDiffChange: 'Change',
  artifactDiffBefore: 'Before',
  artifactDiffAfter: 'After',
  artifactDiffAria: 'Diff: {{added}} added, {{removed}} removed',
  diffLineAdded: 'Added',
  diffLineRemoved: 'Removed',
  diffLineChanged: 'Changed',
  chartAria: '{{type}} chart, {{series}} series, {{points}} points',
  artifactRevisionsLabel: 'Revision history',
  artifactRevisionsEmpty: 'No revisions yet.',
  artifactRevisionLatest: 'latest',
  artifactCompareRevisionAria: 'Compare revision {{version}}',
  artifactProvType: 'Artifact type',
  artifactProvSource: 'Source',
  artifactProvProducedBy: 'Produced by',
  artifactProvRun: 'Run',
  artifactProvNode: 'Node',
  artifactProvTemplate: 'Template',
  artifactProvOwner: 'Owner',
  artifactProvOrg: 'Org',
  artifactOpenWorkbench: 'Open workbench',
  // ADR 0083 — the Library (cross-source artifact gallery)
  libraryEyebrow: 'LIBRARY',
  libraryTitle: 'Library',
  libraryLede: 'Your generated assets — documents, slide decks, images, designs, and media the AI has produced. Open any item to preview, compare revisions, and see its provenance.',
  libraryColName: 'Name',
  libraryColType: 'Type',
  libraryColSource: 'Source',
  libraryColModified: 'Modified',
  librarySourceRun: 'Run output',
  librarySourceDocument: 'Document',
  librarySourceMedia: 'Media',
  librarySearchPlaceholder: 'Search assets…',
  librarySearchAria: 'Search loaded assets by name',
  libraryNoMatch: 'No loaded asset matches',
  libraryClearSearch: 'Clear search',
  libraryAll: 'All',
  libraryImages: 'Images',
  libraryFiles: 'Files',
  // TYPE badge labels (ADR 0083 §Amendment) — asset categories
  libraryType_image: 'Image',
  libraryType_video: 'Video',
  libraryType_audio: 'Audio',
  libraryType_pdf: 'PDF',
  libraryType_file: 'File',
  libraryType_document: 'Document',
  libraryType_slides: 'Slides',
  libraryType_cad: 'CAD',
  libraryType_campaign: 'Ad / campaign',
  libraryType_appDesign: 'App design',
  libraryType_drawing: 'Drawing',
  libraryType_brandKit: 'Brand kit',
  libraryType_productionPlan: 'Production plan',
  libraryType_code: 'Code result',
  libraryType_chart: 'Chart',
  libraryType_diagram: 'Diagram',
  libraryType_interactive: 'Interactive',
  libraryViewTable: 'Table',
  libraryEmpty: 'No assets yet. Generate a document, deck, image, or design and it\'ll appear here.',
  libraryUnavailableTitle: 'Couldn\'t load your library',
  libraryUnavailableBody: 'A loading problem, not an empty library — anything you have generated is still there. Reload before regenerating.',
  libraryMorePagesTitle: 'No matches on this page',
  libraryMorePagesBody: 'None of the loaded items match this filter — load more to keep looking.',
  libraryLoading: 'Loading…',
  libraryLoadMore: 'Load more',
  libraryError: 'Could not load the library. Please try again.',
  modelSwitcherLabel: 'Model',
  modelSwitcherDefault: 'Default model',
  branch: 'Branch',
  branchConversation: 'Branch conversation',
  branchConversationTitle: 'Fork this conversation to continue separately',
  interactiveArtifactTitle: 'Interactive artifact',
  mermaidDiagram: 'Mermaid diagram',
  mermaidSourceLabel: 'Diagram source',
  compare: 'Compare',
  compareTitle: 'Compare conversations',
  mission: 'Mission',
  missionTitle: 'Mission contract',
  compareThis: 'This conversation',
  compareWith: 'Compare with',
  comparePick: 'Pick a conversation…',
  export: 'Export',
  exportAsJson: 'Export as JSON',
  exportConversation: 'Export conversation',
  exportConversationTitle: 'Download this conversation as Markdown',
  exportFailed: 'Couldn’t export the conversation.',
  importFailed: 'Couldn’t import the conversation — check the file and try again.',
  share: 'Share link',
  shareConversationTitle: 'Create a public read-only link to this conversation',
  shareCopied: 'Public link copied to clipboard',
  shareCopyFailed: 'The link was created but couldn’t be copied — copy it now: {{url}}',
  slideImageUnavailable: 'Image unavailable',
  shareCopiedManagedIn: 'Public link copied — manage it under {{org}} → Sharing.',
  shareFailed: 'Could not create a share link.',
  shareOwnerOnly: 'Only the conversation owner can share it.',
  import: 'Import',
  importConversation: 'Import conversation',
  importConversationTitle: "Upload a conversation export as a new conversation (OpenWOP or ChatGPT JSON)",
  importTooLarge: "That file is too large to import (up to 10 MB per conversation).",
  importUnsupported: "That file isn’t a conversation export this app can read. Use an OpenWOP JSON export or a ChatGPT conversations.json.",
  importEmpty: "That export has no messages that can be imported.",
  importNotJson: "That file isn’t JSON. Choose a .json conversation export.",
  importAlreadyImported: "Already imported — opened the existing conversation.",
  importDone_one: "Imported {{count}} message",
  importDone_other: "Imported {{count}} messages",
  importSkipped_one: "{{count}} message skipped (system, tool or empty)",
  importSkipped_other: "{{count}} messages skipped (system, tool or empty)",
  importTruncated_one: "{{count}} message shortened or cut",
  importTruncated_other: "{{count}} messages shortened or cut",
  importArchiveTitle: "Choose a conversation to import",
  importArchiveHint_one: "This ChatGPT export holds {{count}} conversation.",
  importArchiveHint_other: "This ChatGPT export holds {{count}} conversations. Import one at a time.",
  importArchiveFilter: "Filter by title",
  importArchiveNoMatch: "No conversation matches that title.",
  importArchiveUntitled: "Untitled conversation",
  importArchiveMessages_one: "{{count}} message",
  importArchiveMessages_other: "{{count}} messages",
  importArchiveMore: "Showing the {{count}} most recent — filter to find others.",
  importArchiveCancel: "Cancel",
  importedLabel: "Imported",
  importedLabelTitle: "Imported from a file — not written in this app",
  importArchiveMatches_other: "{{count}} conversations",
  importArchiveMatches_one: "{{count}} conversation",
  importInProgress: "Importing the conversation…",
  importConflict: "This file was already imported into a conversation that has since been erased, so it can’t be imported again.",
  importedNotice: "Imported messages are for reference. They aren’t part of the assistant’s context — it starts fresh from your next message.",
  branchFromHere: "Branch from here",
  branchFromHereTitle: "Fork a new conversation seeded through this message",
  artifactEdit: "Edit",
  artifactEditReset: "Reset",
  artifactEditDone: "Done",
  artifactEditSource: "Artifact source",

  // — Multi-tab chat deck (ADR 0140) —
  multiTabNewTabAria: "Open a new chat tab (Alt+N)",
  multiTabKindUnavailable: "This tab type is not available here.",
  multiTabNewTabMenu: "New tab",
  multiTabNewTab: "New chat",
  multiTabTablistAria: "Open chat tabs",
  multiTabCloseTabNamed: "Close {{title}}",
  multiTabRenameTab: "Rename {{title}}",
  multiTabRenameHint: "{{title}} — double-click to rename",
  multiTabEmptyTitle: "No open chats",
  multiTabEmptyBody: "Open a new chat to start working in tabs.",
  multiTabOpenInNewWindow: "Open {{title}} in a new window",
  multiTabPinTab: "Pin {{title}}",
  multiTabUnpinTab: "Unpin {{title}}",
  multiTabPinnedAria: "pinned",
  multiTabUnreadAria: "New reply",
  multiTabBlockedOffscreen: "A tab off-screen is waiting for your input — click to reveal it",
  multiTabBlockedAria: "Waiting for your input",
  multiTabUnreadAnnounce: "New reply in {{title}}",
  multiTabBlockedAnnounce: "{{title}} needs your input",
  multiTabHydrating: "Loading conversation…",
  multiTabPrunedNotice_one: "Closed 1 conversation that was deleted.",
  multiTabPrunedNotice_other: "Closed {{count}} conversations that were deleted.",
  tabEvicted: "Closed \"{{title}}\" to make room — it’s still in your conversations.",
  multiTabDeckFull: "Every open tab is busy, so nothing opened. Close a tab to open another.",
  multiTabLoadingDeck: "Loading your tabs",
  multiTabClosedBusy: "\"{{title}}\" is still in progress. Reopen it from Conversations.",
  hitlVerbApproved: 'Approved',
  hitlVerbClarified: 'Clarified',
  // An agent's clarification nobody answered (skipped, or it expired).
  hitlVerbClarifySkipped: 'Not answered',
  // ADR 1001 D7: the person's answer turn when they skip an agent's chat questions.
  clarifySkippedTurn: 'I skipped your questions.',
  hitlVerbRefined: 'Refined',
  hitlVerbCancelConfirmed: 'Confirmed cancellation',
  hitlVerbReceived: 'Received',
  hitlVerbResolved: 'Resolved',
  hitlVerbRejected: 'Rejected',
  composerAriaLabel: 'Message',
  assistantReplied: 'Assistant reply finished.',
  noPublicChannelsBody: 'When teammates create public channels, they appear here to join.',
  // Voice phase words (CHAT-7/A11Y-8) — composer placeholder + sr status
  voicePhaseConnecting: 'Connecting…',
  voicePhaseLive: 'In conversation',
  voicePhaseListening: 'Listening…',
  voicePhaseTranscribing: 'Transcribing…',
  voicePhaseThinking: 'Thinking…',
  voicePhaseSpeaking: 'Speaking…',
  voicePhaseError: 'Voice error',
  voicePhaseIdle: 'Voice off',
  // Voice agent picker (ADR 0199 P3)
  voiceHeldApprovalExecuted: '\u201c{{tool}}\u201d ran — the result was delivered to the voice session.',
  voiceHeldApprovalDenied: '\u201c{{tool}}\u201d was not allowed.',
  voiceHeldApprovalGone: 'This approval is no longer pending — it was resolved elsewhere or the call ended.',
  voiceHeldApprovalForbidden: 'Only the person on the call, with permission to approve actions, can answer this.',
  voiceApprovalTitle: 'Approve this action?',
  voiceApprovalBody: 'The assistant wants to run \u201c{{tool}}\u201d — a sensitive action that needs your approval.',
  voiceApprovalApprove: 'Approve and run',
  voiceApprovalDeny: 'Don\u2019t allow',
  voicePickerTitle: 'Who do you want to talk to?',
  voicePickerLede: 'A voice conversation works best with one of your agents — it brings its role, memories, workflows, and tools. You can also use the general workspace assistant.',
  voicePickerNoneTitle: 'No agents yet',
  voicePickerNoneBody: 'Create an agent to give voice conversations a role, memories, and tools — or continue with the workspace assistant.',
  voicePickerGeneric: 'Use the workspace assistant',
  // Live boardroom (ADR 0304)
  voicePickerBoardsLabel: 'Boards of advisors',
  voiceSkipTurnTitle: 'Skip this speaker',
  voiceSkipTurnAria: 'Skip the current spoken turn',
  voiceMuteTitle: 'Mute voices',
  voiceUnmuteTitle: 'Unmute voices',
  conversationReviewsHeading: 'Needs your approval (from this conversation)',
  channelAccessLost: 'You no longer have access to this channel — live updates stopped.',
  assetPreviewMediaFailed: 'The media could not be loaded — open the full asset to check it.',
  workflowDraftChip: 'Draft',
  composedEstimate_one: 'Estimated model cost: at least {{usd}} per run ({{count}} AI step — a rough floor, not a quote).',
  composedEstimate_other: 'Estimated model cost: at least {{usd}} per run ({{count}} AI steps — a rough floor, not a quote).',
  composedEstimateTitle: 'A static floor from the workflow\'s AI steps and current model rates, assuming ~1K tokens in and out per step. Real cost depends on the actual inputs.',
  reviewReasoningLabel: 'Agent\'s reasoning (stated by the agent)',
  slaSectionTitle: 'Approval SLA ladder…',
  slaSectionBody: 'Deadlines for pending approvals, applied tenant-wide: remind the approvers, then notify their delegates, then optionally auto-reject. Times are in minutes; leave a rung empty to turn it off.',
  slaLoading: 'Loading the policy…',
  slaLoadFailed: 'Couldn\'t load the SLA policy. Try again in a moment.',
  slaSaveFailed: 'Couldn\'t save the SLA policy. Try again in a moment.',
  slaSaveForbidden: 'Changing the SLA policy requires a workspace admin (members-manage role).',
  slaEnable: 'Enable the ladder',
  slaRemindAfter: 'Remind after (minutes)',
  slaEscalateAfter: 'Escalate after (minutes)',
  slaExpireAfter: 'Auto-reject after (minutes)',
  slaSave: 'Save policy',
  slaSaved: 'Saved.',
  slaLoadRetry: 'Retry loading the policy',
  // ADR 0768 P1 — needs-you tray, turn receipts, composer sigils
  needsYouRegion: "Waiting on you",
  needsYouEyebrow_one: "Needs you · {{count}}",
  needsYouEyebrow_other: "Needs you · {{count}}",
  needsYouMore_one: "+{{count}} more",
  needsYouMore_other: "+{{count}} more",
  needsYouReview: "Review",
  receiptTools_one: "{{count}} tool",
  receiptTools_other: "{{count}} tools",
  receiptReads_one: "{{count}} read",
  receiptReads_other: "{{count}} reads",
  receiptChanges_one: "{{count}} change",
  receiptChanges_other: "{{count}} changes",
  receiptEffectRead: "reads",
  receiptEffectChange: "changes",
  receiptFailed_one: "{{count}} failed",
  receiptFailed_other: "{{count}} failed",
  receiptHandoffs_one: "{{count}} hand-off",
  receiptHandoffs_other: "{{count}} hand-offs",
  receiptShow: "Show what this turn did",
  receiptHide: "Hide what this turn did",
  receiptDetails: "Details",
  sigilWorkflowAria: "Run a workflow",
  sigilWorkflowTitle: "Run a workflow (/)",
  sigilWorkflowBlockedTitle: "Workflows start a new message. Clear the draft to pick one.",
  sigilAgentAria: "Mention an agent",
  sigilAgentTitle: "Hand this to an agent (@)",
  // ADR 0768 P2 — the chat Workbench (runs + artifacts, right-hand pane)
  workbenchAria: "Workbench",
  workbenchEyebrow: "Workbench",
  workbenchTabRun: "Run",
  workbenchTabArtifact: "Artifact",
  workbenchTabSources: "Sources",
  sourcesEmpty: "Nothing cited or attached in this conversation yet.",
  sourcesWeb_one: "{{count}} web source",
  sourcesWeb_other: "{{count}} web sources",
  sourcesFiles_one: "{{count}} file you sent",
  sourcesFiles_other: "{{count}} files you sent",
  sourcesCitedIn_one: "cited in {{count}} turn",
  sourcesCitedIn_other: "cited in {{count}} turns",
  sourcesUnnamedFile: "Unnamed file",
  receiptOpenSources: "View sources in the Workbench",
  workbenchClose: "Close the workbench",
  workbenchShow: "Show the workbench",
  workbenchHide: "Hide the workbench",
  // ADR 0768 P3 — live tab/row status
  multiTabRunningAria: "Still working",
  liveStatusBlocked: "Waiting on you",
  liveStatusRunning: "Running",
  sigilBoardAria: "Convene a board",
  sigilBoardTitle: "Convene a Board of Advisors (@@)",
  // ADR 0768 P4 — embedded → full chat handoff
  handoffLabel: "Continue in full chat",
  handoffTitle: "Open this conversation in the full chat, with its history and agent",
  handoffWaitTitle: "Wait for the reply to finish",
  handoffFailed: "Could not move this conversation. Nothing was lost here; try again.",
  // ADR 0768 P5 — Dock context strip + Pocket
  embedCanSee: "Can see",
  pocketAsk: "Ask {{name}}",
  pocketClose: "Close {{name}}",
  composerMoreActions: "More: attach, workflow, agent",
  // ADR 0768 follow-up P4 — welcome starter-card states
  welcomeCardsLoading: "Loading starter workflows",
  welcomeCardsFailedTitle: "Couldn't load your workflows",
  welcomeCardsFailedBody: "The starter workflows could not be read just now. You can still ask anything below.",
  welcomeCardsNoneTitle: "No starter workflows in this workspace yet",
  welcomeCardsNoneBody: "Add one from the Builder gallery and it will appear here, ready to run with /.",
  welcomeCardsBrowse: "Browse workflows",
  // ADR 0944 D2 — a conversation bound to one client's shared agent memory.
  clientMemoryChip: 'Client memory: {{name}}',
  clientMemoryHelp: 'Agents here share what they remember about this client, and nothing from your other clients.',
  clientLinkOpening: 'Opening a chat for this client…',
  clientLinkFailed: 'That client chat could not be opened: you may not have access to this client, or it no longer exists.',
} as const;
